import { Helmet } from 'react-helmet-async'
import { SITE_URL, toAbsoluteUrl } from '@/lib/seo/absoluteUrl'
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  DEFAULT_OG_IMAGE_HEIGHT,
  DEFAULT_OG_IMAGE_WIDTH,
  DEFAULT_TITLE,
} from '@/lib/seo/siteMeta'

interface SEOProps {
  title?: string
  description?: string
  url?: string
  image?: string
  type?: string
  /** true면 robots noindex 부여 — 빈(0곳)/thin 페이지가 색인되지 않도록 */
  noindex?: boolean
  /**
   * true면 " | 케어밸런스" 를 붙이지 않는다.
   *
   * 브랜드를 앞에 두는 타이틀에 쓴다. 그냥 두면 "케어밸런스 … | 케어밸런스" 가 되어
   * 브랜드가 두 번 나오고, 검색 결과에서 잘리는 자리를 낭비한다.
   */
  brandSuffix?: boolean
}

// 기본 title·description·og:image 의 단일 소스는 lib/seo/siteMeta.ts —
// index.html 폴백과 글자 그대로 같아야 한다 (ssr-spec §17.4.2, siteMeta.test.ts 가 고정)

export default function SEO({
  title, description, url, image, type = 'website', noindex = false, brandSuffix = true,
}: SEOProps) {
  const fullTitle = title ? (brandSuffix ? `${title} | 케어밸런스` : title) : DEFAULT_TITLE
  const desc = description || DEFAULT_DESCRIPTION
  const pageUrl = url ? `${SITE_URL}${url}` : SITE_URL
  // OG/트위터 카드는 절대 URL이어야 한다. 상대 경로(/api/v1/files/...)면 사이트 도메인을 붙인다.
  const ogImage = image ? toAbsoluteUrl(image) : DEFAULT_OG_IMAGE
  // 크기는 기본 이미지일 때만 낸다 — 페이지 이미지(블로그 썸네일 등)는 실제 크기를 몰라
  // 틀린 값이 공유 미리보기를 잘못 자른다 (ssr-spec §17.4.3)
  const isDefaultOgImage = !image

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {noindex && <meta name="robots" content="noindex, follow" />}
      <link rel="canonical" href={pageUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:image" content={ogImage} />
      {isDefaultOgImage && (
        <meta property="og:image:width" content={String(DEFAULT_OG_IMAGE_WIDTH)} />
      )}
      {isDefaultOgImage && (
        <meta property="og:image:height" content={String(DEFAULT_OG_IMAGE_HEIGHT)} />
      )}
      <meta property="og:site_name" content="케어밸런스(CareBalance)" />
      <meta property="og:locale" content="ko_KR" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  )
}
